import { ListChecks, Users } from "lucide-react"
import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { getRoleDisplayName } from "@/constants/roles"
import { formatPhoneDisplay } from "@/features/property/utils/formatPhone"
import { SectionCard } from "@/components/ui/SectionCard"
import { usesTransferWizardCardLayout } from "../../utils/transferPropertyCompanyWizard"
import { TransferSummaryCheckRow } from "../TransferSummaryCheckRow"
import type { TransferSummaryTabProps } from "../../types"

const summaryEmployeesTableWrapClass = "overflow-x-auto rounded-lg border border-slate-200"

function TransferSummarySection({
  useCardLayout,
  icon,
  title,
  testId,
  titleTestId,
  children,
}: {
  useCardLayout: boolean
  icon: LucideIcon
  title: string
  testId?: string
  titleTestId?: string
  children: ReactNode
}) {
  if (!useCardLayout) return <>{children}</>
  return (
    <SectionCard icon={icon} title={title} testId={testId} titleTestId={titleTestId}>
      {children}
    </SectionCard>
  )
}

export function TransferSummaryTab({ ctx }: TransferSummaryTabProps) {
  const {
    selectedTab,
    fragment,
    propertyData,
    loadNewCompany,
    removestaffList,
    transferstaffList,
    addedstaffList,
    newPropertyLabel,
    residentcount,
    applicantcount,
    newPropertyName,
    getValues,
  } = ctx
  if (selectedTab !== "summaryinfo" || !fragment) return null

  const useCardLayout = usesTransferWizardCardLayout(fragment)

  const summaryRows = (
    <div
      data-testid="transfer-process-summary-list"
      className="flex flex-col gap-2.5 break-words"
    >
      <TransferSummaryCheckRow testId="transfer-process-summary-row-dashboard-reports">
        Dashboard Reports Updated
      </TransferSummaryCheckRow>
      {fragment === "transfer_property" && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-transfer-property">
          {String(propertyData?.property_name)} was transferred from {String(getValues("company_name"))} to{" "}
          {String(loadNewCompany?.company_name ?? "")}
        </TransferSummaryCheckRow>
      )}
      {fragment === "transfer_company" && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-transfer-company">
          {String(propertyData?.company_name)} was transferred to {String(loadNewCompany?.company_name ?? "")}
        </TransferSummaryCheckRow>
      )}
      {fragment === "merge_property" && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-merge-property">
          {String(propertyData?.property_name)} was merged into {newPropertyLabel}
        </TransferSummaryCheckRow>
      )}
      {fragment === "split_property" && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-split-property">
          {String(propertyData?.property_name)} was split into {String(newPropertyName || "").trim() || "the new property"}
        </TransferSummaryCheckRow>
      )}
      {!["transfer_resident", "transfer_property", "transfer_company", "split_property", "merge_property"].includes(fragment) && getValues("updatedomain") === "1" && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-email-domain">
          Email domain updated to @{getValues("newdomain")}
        </TransferSummaryCheckRow>
      )}
      {fragment !== "transfer_resident" && addedstaffList.length > 0 && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-employees-added">
          {addedstaffList.length} Employee Added
        </TransferSummaryCheckRow>
      )}
      {!["split_property", "transfer_resident"].includes(fragment) && removestaffList.length > 0 && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-employees-removed">
          {removestaffList.length} Employees Removed
        </TransferSummaryCheckRow>
      )}
      {fragment !== "transfer_resident" && transferstaffList.length > 0 && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-employees-transferred">
          {transferstaffList.length} Employees Transferred
        </TransferSummaryCheckRow>
      )}
      {residentcount > 0 && applicantcount === 0 && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-residents-only">
          Transferred {residentcount} Residents
        </TransferSummaryCheckRow>
      )}
      {residentcount === 0 && applicantcount > 0 && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-applicants-only">
          Transferred {applicantcount} Applicants
        </TransferSummaryCheckRow>
      )}
      {residentcount > 0 && applicantcount > 0 && (
        <TransferSummaryCheckRow testId="transfer-process-summary-row-residents-and-applicants">
          Transferred {residentcount} Residents, {applicantcount} Applicants
        </TransferSummaryCheckRow>
      )}
    </div>
  )

  const employeesTable = (
    <div
      data-testid="transfer-process-summary-employees-table-container"
      className={useCardLayout ? summaryEmployeesTableWrapClass : "overflow-x-auto rounded border border-slate-100"}
    >
      <table
        data-testid="transfer-process-summary-employees-table"
        className="min-w-[520px] w-full text-left text-xs"
      >
        <thead
          data-testid="transfer-process-summary-employees-thead"
          className={useCardLayout ? "bg-slate-50/95" : undefined}
        >
          <tr data-testid="transfer-process-summary-employees-header-row">
            <th
              data-testid="transfer-process-summary-employees-col-action"
              className={cn("p-2", useCardLayout && "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500")}
            >
              Action
            </th>
            <th
              data-testid="transfer-process-summary-employees-col-name"
              className={cn("p-2", useCardLayout && "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500")}
            >
              Name
            </th>
            <th
              data-testid="transfer-process-summary-employees-col-role"
              className={cn("p-2", useCardLayout && "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500")}
            >
              Role
            </th>
            <th
              data-testid="transfer-process-summary-employees-col-phone"
              className={cn("p-2", useCardLayout && "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500")}
            >
              Phone Number
            </th>
            <th
              data-testid="transfer-process-summary-employees-col-email"
              className={cn("p-2", useCardLayout && "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500")}
            >
              Email
            </th>
          </tr>
        </thead>
        <tbody className={useCardLayout ? "divide-y divide-slate-100" : undefined}>
          {removestaffList.map((s) => (
            <tr
              key={`d-${s.role_id}`}
              data-testid={`transfer-process-summary-deleted-row-${s.role_id}`}
              className={fragment === "split_property" ? "hidden" : ""}
            >
              <td className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-800")}>Deleted</td>
              <td
                className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-800")}
                data-testid={`transfer-process-summary-deleted-name-${s.role_id}`}
              >
                {s.name ?? "—"}
              </td>
              <td
                className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-700")}
                data-testid={`transfer-process-summary-deleted-role-${s.role_id}`}
              >
                {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
              </td>
              <td className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-600")}>
                {formatPhoneDisplay(s.mobile_no) || "—"}
              </td>
              <td className={cn("p-2 break-all", useCardLayout && "px-3 py-2.5 text-slate-600")}>
                {s.email ?? "—"}
              </td>
            </tr>
          ))}
          {transferstaffList.map((s) => (
            <tr
              key={`t-${s.role_id}`}
              data-testid={`transfer-process-summary-transferred-row-${s.role_id}`}
            >
              <td className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-800")}>Transferred</td>
              <td
                className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-800")}
                data-testid={`transfer-process-summary-transferred-name-${s.role_id}`}
              >
                {s.name ?? "—"}
              </td>
              <td
                className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-700")}
                data-testid={`transfer-process-summary-transferred-role-${s.role_id}`}
              >
                {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
              </td>
              <td className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-600")}>
                {formatPhoneDisplay(s.mobile_no) || "—"}
              </td>
              <td className={cn("p-2 break-all", useCardLayout && "px-3 py-2.5 text-slate-600")}>
                {s.email ?? "—"}
              </td>
            </tr>
          ))}
          {addedstaffList.map((s) => (
            <tr
              key={`a-${s.role_id}`}
              data-testid={`transfer-process-summary-added-row-${s.role_id}`}
            >
              <td className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-800")}>Added</td>
              <td
                className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-800")}
                data-testid={`transfer-process-summary-added-name-${s.role_id}`}
              >
                {s.name ?? "—"}
              </td>
              <td
                className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-700")}
                data-testid={`transfer-process-summary-added-role-${s.role_id}`}
              >
                {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
              </td>
              <td className={cn("p-2", useCardLayout && "px-3 py-2.5 text-slate-600")}>
                {formatPhoneDisplay(s.mobile_no) || "—"}
              </td>
              <td className={cn("p-2 break-all", useCardLayout && "px-3 py-2.5 text-slate-600")}>
                {s.email ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const summaryPanelGridClass = useCardLayout
    ? "grid min-w-0 gap-4 lg:grid-cols-2 lg:items-start animate-in fade-in duration-300"
    : "grid min-w-0 gap-6 md:grid-cols-2"

  return (
        <div
          data-testid="transfer-process-summary-tab-panel"
          className={summaryPanelGridClass}
        >
          <TransferSummarySection
            useCardLayout={useCardLayout}
            icon={ListChecks}
            title="Summary"
            testId="transfer-process-summary-section"
            titleTestId="transfer-process-summary-heading"
          >
          <div data-testid="transfer-process-summary-column-primary" className="min-w-0">
            {!useCardLayout && (
            <h3 data-testid="transfer-process-summary-heading" className="mb-2 font-semibold">
              Summary
            </h3>
            )}
            {summaryRows}
          </div>
          </TransferSummarySection>

          {fragment !== "transfer_resident" && (
            <TransferSummarySection
              useCardLayout={useCardLayout}
              icon={Users}
              title="Employees Added/Deleted"
              testId="transfer-process-summary-employees-section"
              titleTestId="transfer-process-summary-employees-heading"
            >
            <div data-testid="transfer-process-summary-column-employees" className="min-w-0">
              {!useCardLayout && (
              <h3
                data-testid="transfer-process-summary-employees-heading"
                className="mb-2 font-semibold"
              >
                Employees Added/Deleted
              </h3>
              )}
              {employeesTable}
            </div>
            </TransferSummarySection>
          )}
        </div>
  )
}
